import type { DealerSegmentRow } from 'app/pages/DealerSegmentSettingsComponent/components/DealerSegmentModal';

const EXPECTED_HEADERS = [
  'field_name',
  'field_value',
  'qualifier',
  'primary_segment',
  'secondary_segment',
  'tertiary_segment',
  'priority',
] as const;

function parseCSVLine(line: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;
  while (i < line.length) {
    const char = line[i];
    const nextChar = line[i + 1];
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i += 2;
        continue;
      }
      inQuotes = !inQuotes;
      i += 1;
      continue;
    }
    if (!inQuotes && (char === ',' || char === '\r')) {
      values.push(current.trim());
      current = '';
      i += 1;
      continue;
    }
    if (!inQuotes && char === '\n') {
      values.push(current.trim());
      current = '';
      i += 1;
      break;
    }
    current += char;
    i += 1;
  }
  if (current !== '' || values.length > 0) values.push(current.trim());
  return values;
}

function normalizeHeader(h: string): string {
  return h
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');
}

export interface ParseSegmentsResult {
  rows: DealerSegmentRow[];
  errors: string[];
}

export function parseCSVToSegments(csvText: string): ParseSegmentsResult {
  const errors: string[] = [];
  const lines = csvText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) {
    return { rows: [], errors: ['CSV must have a header row and at least one data row.'] };
  }
  const rawHeaders = parseCSVLine(lines[0]);
  const headerMap: Record<string, number> = {};
  rawHeaders.forEach((h, i) => {
    const key = normalizeHeader(h);
    if (key && headerMap[key] === undefined) headerMap[key] = i;
  });
  const rows: DealerSegmentRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    if (values.every(v => !v || v.trim() === '')) continue;
    const get = (key: string) => {
      const idx = headerMap[key] ?? headerMap[key.replace(/_/g, ' ')];
      const v = idx !== undefined ? values[idx] : '';
      return (v ?? '').trim();
    };
    const priorityNum = parseInt(get('priority'), 10);
    rows.push({
      field_name: get('field_name'),
      field_value: get('field_value'),
      qualifier: get('qualifier') || 'Starts with',
      primary_segment: get('primary_segment'),
      secondary_segment: get('secondary_segment'),
      tertiary_segment: get('tertiary_segment'),
      priority: Number.isFinite(priorityNum) && priorityNum >= 1 ? priorityNum : 1,
    });
  }
  return { rows, errors };
}

export const CSV_TEMPLATE_HEADERS = EXPECTED_HEADERS.join(',');
